import axios from 'axios';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { Formik, FormikProps } from 'formik';
import { useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { userEditValidationSchema } from 'static/validations/userEditValidationSchema';
import * as yup from 'yup';

// Define the form values type
interface UserFormValues {
  userName: string;
  userId: string;
  subscribeEmailId: string;
}

// Dynamic Edit Modal Component
const UserEditModal = ({
  show,
  onHide,
  user,
  onSuccess
}: {
  show: boolean;
  onHide: () => void;
  user: any;
  onSuccess: any;
}) => {
  const schema = yup.object().shape(userEditValidationSchema);

  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');

  // Create initial values that update when user changes
  const getInitialValues = (): UserFormValues => ({
    userName: user?.userName || '',
    userId: user?.userId || '',
    subscribeEmailId: user?.subscribeEmailId || ''
  });

  const onSubmit = async (values: UserFormValues) => {
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/users/${user.id}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 'error') {
        toast.error(response.data.message);
        return;
      }

      toast.success('User updated successfully');
      onSuccess();
      onHide();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const closeEditModal = () => {
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FeatherIcon icon="edit-2" size={20} className="me-2" />
          Edit User: {user?.userName}
        </Modal.Title>
      </Modal.Header>

      <Formik<UserFormValues>
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={getInitialValues()}
        enableReinitialize={true} // This will reinitialize when user prop changes
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors
        }: FormikProps<UserFormValues>) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col xl={12}>
                  <Row className={classNames('g-3', 'mb-4')}>
                    <Form.Group className="mb-3">
                      <Form.Label>User ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.userId}
                        disabled
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="userName"
                        value={values.userName}
                        onChange={handleChange}
                        isValid={touched.userName && !errors.userName}
                        isInvalid={touched.userName && !!errors.userName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.userName}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Subscribe Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="subscribeEmailId"
                        value={values.subscribeEmailId}
                        onChange={handleChange}
                        isValid={
                          touched.subscribeEmailId && !errors.subscribeEmailId
                        }
                        isInvalid={
                          touched.subscribeEmailId && !!errors.subscribeEmailId
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subscribeEmailId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={closeEditModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="save" size={16} className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default UserEditModal;
